import {
	updateAiFinances,
	teamBaseballBudget,
} from "../team/aiFinances.baseball.ts";
import { resolveMarkets } from "../contractNegotiation/market.ts";
import { PLAYER } from "../../../common/constants.ts";
import { player, team } from "../index.ts";
import getBest from "./getBest.ts";
import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import { orderBy } from "../../../common/utils.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { trimBaseballRoster } from "../team/manageRoster.baseball.ts";
import normalizeContractDemands from "./normalizeContractDemands.ts";
import { shuffle } from "../../../common/random.ts";
import type { Player } from "../../../common/types.ts";
import {
	isBigClubEligible,
	createRosterScoreCache,
} from "../team/rosterComposition.baseball.ts";
import { MINORS_MAX_AGE } from "../team/manageRoster.baseball.ts";

/**
 * AI teams sign free agents.
 *
 * Each team (in random order) will sign free agents up to their salary cap or roster size limit. This should eventually be made smarter
 *
 * @memberOf core.freeAgents
 * @return {Promise}
 */
const autoSign = async () => {
	await updateAiFinances();
	await resolveMarkets();
	const players = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.FREE_AGENT,
	);

	if (players.length === 0) {
		return;
	}

	// List of free agents, sorted by value
	let playersSorted = orderBy(players, "value", "desc");

	// All clubs compare the same free-agent ratings today. Reuse only those
	// scores; team needs, budgets and exact assignments remain team-specific.
	const scoreCandidate = isSport("baseball")
		? createRosterScoreCache(g.get("maxRosterSize"), g.get("dh") !== "none")
		: undefined;

	// Randomly order teams
	const teams = await idb.cache.teams.getAll();
	shuffle(teams);

	for (const t of teams) {
		// Skip the user's team
		if (
			g.get("userTids").includes(t.tid) &&
			!local.autoPlayUntil &&
			!g.get("spectator")
		) {
			continue;
		}

		if (t.disabled) {
			continue;
		}

		let probSkip;
		if (isSport("basketball")) {
			probSkip = t.strategy === "rebuilding" ? 0.9 : 0.75;
		} else {
			probSkip = 0.5;
		}

		// Skip teams sometimes
		if (Math.random() < probSkip) {
			continue;
		}

		const playersOnRoster = await idb.cache.players.indexGetAll(
			"playersByTid",
			t.tid,
		);

		// With forceHistoricalRosters, only sign FAs if we have to
		if (
			playersOnRoster.length >= g.get("minRosterSize") &&
			g.get("forceHistoricalRosters")
		) {
			continue;
		}

		// Ignore roster size, will drop bad player if necessary in checkRosterSizes, and getBest won't sign min contract player unless under the roster limit
		const payroll = await team.getPayroll(t.tid);
		const p = getBest(
			playersOnRoster,
			playersSorted,
			payroll,
			scoreCandidate,
			isSport("baseball") ? await teamBaseballBudget(t.tid) : undefined,
		);
		if (p) {
			// Remove from list of free agents
			playersSorted = playersSorted.filter((p2) => p2 !== p);

			await player.sign(p, t.tid, p.contract, g.get("phase"));
			await idb.cache.players.put(p);
			if (
				isSport("baseball") &&
				playersOnRoster.length + 1 > g.get("maxRosterSize")
			) {
				const released = await trimBaseballRoster(
					[...playersOnRoster, p],
					playersOnRoster.length + 1 - g.get("maxRosterSize"),
				);
				if (released.length)
					await normalizeContractDemands({
						type: "dummyExpiringContracts",
						pids: released,
					});
			}
			await team.rosterAutoSort(t.tid);
		}

		// Young prospects, the 16 and 17 year old international signings above all, can't
		// join a big club, so clubs sign the best ones straight into their farm system
		if (isSport("baseball")) {
			const prospect = await signProspectToMinors(t.tid, playersSorted);
			if (prospect) {
				playersSorted = playersSorted.filter((p2) => p2 !== prospect);
			}
		}
	}
};

// Farm systems stay a manageable size; prospects need upside and a cheap contract
const MAX_MINORS = 15;
const MIN_PROSPECT_UPSIDE = 10;
const signProspectToMinors = async (tid: number, freeAgents: Player[]) => {
	const minors = (
		await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS)
	).filter((p) => p.minorTid === tid);
	if (minors.length >= MAX_MINORS) {
		return;
	}
	const season = g.get("season");
	const prospect = orderBy(
		freeAgents.filter((p) => {
			const r = p.ratings.at(-1)!;
			return (
				season - p.born.year < MINORS_MAX_AGE &&
				!isBigClubEligible(p) &&
				r.pot - r.ovr >= MIN_PROSPECT_UPSIDE &&
				p.contract.amount <= 2 * g.get("minContract")
			);
		}),
		(p) => p.ratings.at(-1)!.pot,
		"desc",
	)[0];
	if (!prospect) {
		return;
	}
	// player.sign places anyone up to 18 in the minors
	await player.sign(prospect, tid, prospect.contract, g.get("phase"));
	await idb.cache.players.put(prospect);
	return prospect;
};

export default autoSign;
